import { ArrowRight, X } from "lucide-react";

/**
 * TourTooltip — the floating card portion of OnboardingTour. Renders the
 * step icon, eyebrow ("2 / 4"), title, body, progress dots, skip link and
 * next/got-it CTA. Positioned by inline `style` from the parent.
 */
export default function TourTooltip({
  t,
  style,
  step,
  IconEl,
  idx,
  totalSteps,
  onNext,
  onSkip,
}) {
  const isLast = idx >= totalSteps - 1;
  return (
    <div
      className="fixed animate-fadeup"
      style={{
        ...style,
        background: "linear-gradient(180deg, #FFFFFF 0%, #FDFCFB 100%)",
        borderRadius: 16,
        boxShadow: "0 24px 60px rgba(15,20,25,0.35), 0 4px 12px rgba(15,20,25,0.2)",
        border: "1px solid rgba(224,158,80,0.35)",
        padding: 20,
        color: "var(--text-primary)",
      }}
      data-testid="tour-tooltip"
    >
      <div className="flex items-start gap-3">
        <div
          className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(45,90,64,0.10)", color: "#2D5A40" }}
        >
          <IconEl size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] tracking-[0.22em] uppercase"
            style={{ color: "#576A5E" }}
          >
            {t("tour.eyebrow", { current: idx + 1, total: totalSteps })}
          </div>
          <h3
            className="text-lg font-medium leading-tight mt-1 tracking-tight"
            style={{ fontFamily: "var(--font-heading)" }}
            data-testid="tour-title"
          >
            {step.title}
          </h3>
        </div>
        <button
          onClick={onSkip}
          className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          title={t("tour.skip_title")}
          data-testid="tour-skip-btn"
        >
          <X size={16} />
        </button>
      </div>

      <p className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">
        {step.body}
      </p>

      {/* Progress dots */}
      <div className="flex items-center gap-1.5 mt-4">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <span
            key={`dot-${i}`}
            className="rounded-full transition-all"
            style={{
              height: 4,
              width: i === idx ? 22 : 6,
              background: i === idx ? "#2D5A40" : "rgba(45,90,64,0.25)",
            }}
          />
        ))}
      </div>

      <div className="flex items-center justify-between mt-4">
        <button
          onClick={onSkip}
          className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          data-testid="tour-skip-link"
        >
          {t("tour.skip_link")}
        </button>
        <button
          onClick={onNext}
          className="btn-primary text-sm"
          data-testid="tour-next-btn"
        >
          {isLast ? t("tour.got_it") : t("tour.next")}
          <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}
